import { ReactNode, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import {
  AccessDeniedScreen,
  AuthErrorScreen,
  AuthenticatingScreen,
  SignInScreen,
} from '../auth/thresholds'

interface ConsultantGuardProps {
  children: ReactNode
}

/**
 * Route guard for the consultant workspace. Renders the SPEC-018 A4 cinematic
 * auth thresholds (ADR-084) with the consultant audience copy, and sends admins
 * without a consultant role to /admin instead of showing them a denial.
 * Token acquisition is unchanged (ADR-085 — MSAL stays).
 *
 * @param children - The protected consultant content.
 * @returns The appropriate threshold screen, or the protected content.
 */
export function ConsultantGuard({ children }: ConsultantGuardProps) {
  const { isAuthenticated, isLoading, user, error, login, logout } = useAuth()
  const navigate = useNavigate()

  // Store return URL when not authenticated so login can bounce the user back.
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      const returnUrl = window.location.pathname + window.location.search
      sessionStorage.setItem('auth_return_url', returnUrl)
    }
  }, [isLoading, isAuthenticated, error])

  // Route Admin users (without a Consultant role) to their own console.
  useEffect(() => {
    if (!isLoading && isAuthenticated && user && !user.roles.includes('Consultant')) {
      if (user.roles.includes('Admin')) {
        navigate('/admin', { replace: true })
      }
    }
  }, [isLoading, isAuthenticated, user, navigate])

  if (isLoading) {
    return <AuthenticatingScreen testId="consultant-auth-loading" />
  }

  if (error) {
    return <AuthErrorScreen error={error} onRetry={login} />
  }

  if (!isAuthenticated) {
    return <SignInScreen audience="consultant" onSignIn={login} ctaTestId="consultant-login-button" />
  }

  if (!user?.roles.includes('Consultant')) {
    return (
      <AccessDeniedScreen
        audience="consultant"
        testId="consultant-access-denied"
        onHome={() => {
          window.location.href = '/'
        }}
        onSignOut={logout}
      />
    )
  }

  return <>{children}</>
}

export default ConsultantGuard
